import React, { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { useToast } from '@pw-tech/omni-ui';
import useUploadFile from '@/hooks/useFileUpload';
import { Eye, Info, Trash2 } from 'lucide-react';
import { erpBaseUrl } from '@/utils/env.constants';
const normalizeUrl = (url: string): string => {
  if (url.startsWith('/api/')) {
    return `${erpBaseUrl}${url}`;
  }
  return url;
};
interface FileUploadProps {
  className?: string;
  label?: string;
  description?: string;
  acceptType?: string[];
  maxSize?: number;
  onChange?: (data: any) => void;
  disabled?: boolean;
  onUploadComplete: (uploadedFileData: any) => void;
  onRemove: () => void;
  defaultValue?: string;
}
const FileUploadComponent: React.FC<FileUploadProps> = ({
  label,
  description = 'File formats supported: .pdf, .png or .jpg ',
  acceptType = ['pdf', 'png', 'jpg', 'jpeg'],
  className = '',
  maxSize,
  onChange,
  disabled = false,
  onUploadComplete,
  onRemove,
  defaultValue = ''
}) => {
  const [uploadedFileData, setUploadedFileData] = useState<any>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const {
    isLoading: isUploading,
    mutate: uploadFile
  } = useUploadFile();
  const {
    toast: showToaster
  } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileUploaded = e.target.files?.[0];
    if (!fileUploaded) return;
    setUploadProgress(20);
    const fileExtension = fileUploaded.name.split('.').pop()?.toLowerCase();
    if (!acceptType.includes(fileExtension || '')) {
      const errMsg = `Only ${acceptType.join(', ').toUpperCase()} files are supported`;
      showToaster({
        message: errMsg,
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        }
      });
      return;
    }
    const fsizeInBytes = fileUploaded.size;
    const fsizeInKB = Math.round(fsizeInBytes / 1024);
    const fsizeInMB = Math.round(fsizeInBytes / (1024 * 1024) * 100) / 100; // Round to 2 decimal places

    if (typeof maxSize === 'number' && fsizeInKB > maxSize) {
      setUploadProgress(0);
      const maxSizeInMB = Math.round(maxSize / 1024 * 100) / 100; // Convert KB to MB with 2 decimal places
      const errorMessage = `File size (${fsizeInMB} MB) cannot be greater than ${maxSizeInMB} MB`;
      showToaster({
        message: errorMessage,
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        }
      });
      return;
    }
    uploadFile({
      file: fileUploaded,
      onUploadProgress: (progress: number) => {
        setUploadProgress(progress);
      }
    }, {
      onSuccess: (result: any) => {
        const fileData = result?.data?.data;
        const normalizedFileData = {
          ...fileData,
          fullUrl: normalizeUrl(fileData?.fullUrl || '')
        };
        setUploadedFileData(normalizedFileData);
        onUploadComplete(result);
        setUploadProgress(0);
        showToaster({
          message: 'File uploaded successfully!',
          variant: 'success',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center'
          }
        });
      },
      onError: (err: any) => {
        setUploadedFileData(null);
        setUploadProgress(0);
        showToaster({
          message: err?.message ?? 'File Upload Error',
          variant: 'error',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center'
          }
        });
      }
    });
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    setIsDragOver(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e?.dataTransfer?.files?.length) {
      const dragEvent = {
        target: {
          files: e.dataTransfer.files,
          value: ''
        }
      } as React.ChangeEvent<HTMLInputElement>;
      handleFileInput(dragEvent);
    }
  };
  const removeFile = () => {
    setUploadedFileData(null);
    onRemove();
  };
  const handlePreview = () => {
    if (uploadedFileData?.fullUrl) {
      window.open(uploadedFileData?.fullUrl, '_blank');
    }
  };
  const openFileDialog = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  // Add useEffect to handle defaultValue
  useEffect(() => {
    if (defaultValue && !uploadedFileData) {
      // Extract filename from URL or use a default name
      const fileName = defaultValue.split('/').pop() || 'uploaded-file';
      setUploadedFileData({
        name: fileName,
        fullUrl: normalizeUrl(defaultValue)
      });
    }
  }, [defaultValue, uploadedFileData]);
  return <div className="w-full" data-sentry-component="FileUploadComponent" data-sentry-source-file="FileUploadComponent.tsx">
      <div className="mb-2 text-sm font-medium text-[#757575]">{label}</div>

      {/* File Display */}
      {uploadedFileData?.name ? <div className="w-full">
          <div className="relative w-full overflow-hidden border border-gray-200 rounded-lg">
            <div className="relative flex items-center justify-between w-full p-3">
              {/* File icon and file info */}
              <div className="flex items-start flex-1 w-0 gap-2 overflow-hidden">
                <Image src="/images/registerPage/Pdf.png" alt="pdf-icon" width={28} height={28} />
                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {uploadedFileData?.name}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {uploadedFileData?.fullUrl && <Eye size={20} onClick={handlePreview} className="text-gray-600 cursor-pointer hover:text-blue-600" />}
                <Trash2 size={20} onClick={removeFile} className="text-gray-600 cursor-pointer hover:text-red-600" />
              </div>
            </div>
          </div>
        </div> : <div className="w-full">
          <div className={`relative 
            border border-gray-300 rounded-lg px-4 py-3 gap-2
            ${isDragOver ? 'border-blue-400 ' : 'hover:border-gray-400'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
             cursor-pointer transition-all duration-200
            flex items-center justify-center text-sm text-gray-500
          `} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            <input ref={fileInputRef} type="file" accept={acceptType.map(format => `.${format}`).join(',')} onChange={handleFileInput} className="hidden" disabled={disabled} />

            <div className="flex flex-wrap items-center gap-2 text-center">
              <div className="flex items-center justify-center w-5 h-6 rounded-full">
                <Image src="/images/registerPage/cloud-arrow-up.png" alt="upload-icon" width={28} height={28} />
              </div>
              <span>Drop file here or</span>
              <button type="button" onClick={openFileDialog} disabled={disabled} className="text-blue-600 underline hover:text-blue-800">
                Browse Files
              </button>
            </div>
            {/* Progress Bar */}
            {uploadProgress ? <div className="absolute bottom-0 left-0 w-full h-1 bg-[#EAECEF] rounded-b-full mx-[2px]">
                <div className="h-full transition-all duration-500 ease-out bg-[#3CCB7F] rounded-bl-full rounded-tr-full overflow-hidden " style={{
            width: `${uploadProgress}%`
          }} />
              </div> : <></>}
          </div>
        </div>}

      {/* Description */}
      <div className="flex items-center gap-1 py-1">
        <Info size={18} data-sentry-element="Info" data-sentry-source-file="FileUploadComponent.tsx" />
        <div className="text-xs text-[#757575]">{description}</div>
      </div>
    </div>;
};
export default FileUploadComponent;